import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useLbeAuth } from '@laborelec/lbe-react/auth';
import { useCallback, useState } from 'react';
import { lightTheme } from '../styles/theme';

const LoginPage = () => {
  const { login } = useLbeAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = useCallback(async () => {
    setIsLoading(true);
    try {
      await login({ originalUri: '/' });
    } catch {
      setIsLoading(false);
    }
  }, [login]);

  return (
    <ThemeProvider theme={lightTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #002A5C 0%, #0087C3 50%, #009DE0 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-15%',
            right: '-10%',
            width: '50vw',
            height: '50vw',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-20%',
            left: '-15%',
            width: '60vw',
            height: '60vw',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            mx: 2,
            px: { xs: 4, sm: 5 },
            py: { xs: 5, sm: 6 },
            bgcolor: '#FFFFFF',
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0,42,92,0.25), 0 8px 24px rgba(0,42,92,0.12)',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'center', alignItems: 'center', mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #009DE0 0%, #0087C3 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,157,224,0.3)',
              }}
            >
              <StorageIcon sx={{ fontSize: 26, color: '#fff' }} />
            </Box>
          </Stack>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: '#002A5C',
              letterSpacing: '-0.02em',
              fontSize: '1.75rem',
              mb: 0.5,
            }}
          >
            TechnoDB
          </Typography>

          <Typography variant="body2" sx={{ color: '#5C6B7A', mb: 4, fontSize: '0.875rem' }}>
            Battery Technology Database
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#DEE3EA' }} />
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#F5F7FA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #DEE3EA',
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 18, color: '#8E99A8' }} />
            </Box>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#DEE3EA' }} />
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleLogin}
            disabled={isLoading}
            fullWidth
            sx={{
              py: 1.5,
              fontSize: '0.95rem',
              fontWeight: 700,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #009DE0 0%, #0087C3 100%)',
              boxShadow: '0 4px 14px rgba(0,157,224,0.35)',
              textTransform: 'none',
              letterSpacing: '0.01em',
              '&:hover': {
                background: 'linear-gradient(135deg, #0087C3 0%, #006DA3 100%)',
                boxShadow: '0 6px 20px rgba(0,157,224,0.45)',
              },
              '&.Mui-disabled': {
                background: '#B0BAC5',
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            ) : (
              'Sign in with Okta'
            )}
          </Button>

          <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#B0BAC5' }}>
            Secured with Okta SSO &middot; PKCE
          </Typography>
        </Box>

        <Typography
          variant="caption"
          sx={{
            mt: 4,
            color: 'rgba(255,255,255,0.5)',
            fontSize: '0.7rem',
            position: 'relative',
            zIndex: 1,
          }}
        >
          &copy; {new Date().getFullYear()} Laborelec &middot; ENGIE
        </Typography>
      </Box>
    </ThemeProvider>
  );
};

export default LoginPage;
